// Document uploads resource — the list behind the "Document Uploads" section,
// the sibling of the cases list. A document upload is a Project subtype like a
// case, so its detail page reads tasks by project_id and renders them with the
// same TaskTable the case page uses.

import { queryOptions } from "@tanstack/react-query";

import { halFetch, type HalCollection, type HalResource } from "~/lib/hal";
import { openMatchGate, type Task } from "~/lib/tasks";
import type { BadgeVariant } from "~/components/Badge";

/** The document-uploads side-table fields embedded under each segment
 *  (`documents.metadata`). `filed_at` is the re-file idempotency marker — set
 *  once a segment uploads to Filevine — which is how status distinguishes a
 *  fully-filed upload from one still in flight. */
export interface UploadDocumentMetadata {
  matched_project_id?: number | null;
  filed_at?: string | null;
}

/** The case a document was matched into, embedded under `documents.case`
 *  (models.DocumentEmbeds.Case). Present on the upload DETAIL only — the list
 *  stays join-free — and only once `set document case project id` has run. */
export interface UploadDocumentCase {
  id: string;
  project_id: string;
}

/** One document of an upload — a row from the upload's embedded `documents`.
 *  Under the files-are-documents model every dropped file is a document row from
 *  confirm time: images and single-run PDFs are their own leaf; a multi-run PDF
 *  is a PARENT whose per-run CHILDREN (parent_document_id set) the split consumer
 *  creates. Both the List (with the Documents preload) and the View carry these,
 *  so the index and detail derive their count + status from the same shape. */
export interface UploadDocument {
  id: string;
  name: string;
  /** Mirrors the Go model's `type` json tag (enums.DocumentType) — WHAT the
   *  document is, never where it came from (that is `origin`). */
  type: string;
  /** How the document came into being (enums.DocumentOrigin): a dropped file, a
   *  segment the bundle split cut out of one, or a form the Client Agreement
   *  split extracted from a signed packet. Server-stamped at insert, so it is
   *  the one trustworthy provenance signal on the row. */
  origin: string;
  /** The document this one was extracted from. Set on both kinds of split child
   *  — a multi-run PDF's per-run segments AND a packet's forms (which inherit
   *  the upload's project). Mirrors the Go model's `parent_document_id` json
   *  tag. It does NOT distinguish the two: `origin` does. */
  parent_document_id?: string | null;
  /** The matched case's project — set by `set document case project id` once
   *  the case match is resolved. Non-null means the document's remaining work
   *  (the review gates, then filing) happens on the CASE, not on this upload.
   *  A plain documents column, so it rides both the list and the detail. */
  case_project_id?: string | null;
  created_at?: string | null;
  _embedded?: {
    metadata?: UploadDocumentMetadata | null;
    case?: UploadDocumentCase | null;
  };
}

/** The origin of a form the Client Agreement split extracted from a signed
 *  packet: it files via the CASE side, never the upload. Mirrors
 *  enums.DocumentOriginPacketForm in api/app/enums/document_origin.go (the Go
 *  source of truth) — keep this literal copy in lockstep across the language
 *  boundary. Nothing else on the row can stand in for it (TMF-393): the same
 *  Designee Authorization / HIPAA type arriving as a dropped file or as one
 *  segment of a scanned bundle is ordinary work with an arm of its own. */
const PACKET_FORM_ORIGIN = "Packet Form";

function isPacketForm(d: UploadDocument): boolean {
  return d.origin === PACKET_FORM_ORIGIN;
}

/** A document upload. `project_id` scopes its tasks (the detail page lists
 *  `?project_id=…`, exactly like a case). `path` is the uploaded file's S3 key.
 *  `documents` are the split segments (the index List and detail View both
 *  embed them — see documentUploadsRepo.Filter / .first). */
export type DocumentUpload = HalResource<{
  id: string;
  project_id: string;
  /** The label the scanner sent the drop under, which is what a list of
   *  batches renders. Its own column (TMF-512). */
  name: string;
  path: string;
  created_at?: string | null;
  _embedded?: {
    project?: { id: string };
    documents?: UploadDocument[];
  };
}>;

/** The documents a human actually dropped: the upload's parentless rows. Under
 *  the files-are-documents model that IS the manifest — one row per dropped file
 *  (a multi-run PDF parent, a single-run PDF, or an image). Parentless ⇒ never a
 *  split child (manifest children and case-born Designee/HIPAA both carry a
 *  parent_document_id), so the count is exactly the file drop. This drives the
 *  "Documents" count on the index and detail. Status derives from leafDocuments,
 *  not this — a multi-run PDF parent never files, so counting it toward status
 *  would strand the upload short of Complete. */
export function droppedDocuments(upload: DocumentUpload): UploadDocument[] {
  return (upload._embedded?.documents ?? []).filter(
    (d) => d.parent_document_id == null,
  );
}

/** The pipeline's working set: the leaves of the manifest tree, over which
 *  status is aggregated. A document is a leaf iff
 *    (a) no OTHER manifest document names it as parent — a multi-run PDF parent
 *        is disqualified by its per-run segments; a document whose ONLY children
 *        are a packet's forms stays a leaf (it files itself), AND
 *    (b) it is not itself a packet form (origin "Packet Form") — those file via
 *        the case side, never the upload. Provenance comes from `origin`, never
 *        from type + parent: a designee/HIPAA that was dropped as its own file
 *        or cut out of a scanned bundle IS a leaf (TMF-393).
 *  This mirrors uploadedDocumentNodes in api/app/services/projects/loading.go
 *  (the backend workflow composer). The two MUST stay semantically identical:
 *  the composer decides which documents grow upload-side arms, and this decides
 *  which documents' filed_at rolls up into the badge — a drift would show a
 *  "Complete" badge for an upload the pipeline still has arms open on, or vice
 *  versa. Derived in memory from the embedded set, like the composer. */
export function leafDocuments(upload: DocumentUpload): UploadDocument[] {
  const documents = upload._embedded?.documents ?? [];

  // Only segments disqualify their parent. A packet form points at the
  // classified packet, which stays the leaf.
  const referencedParents = new Set<string>();
  for (const d of documents) {
    if (d.parent_document_id != null && !isPacketForm(d)) {
      referencedParents.add(d.parent_document_id);
    }
  }

  return documents.filter(
    (d) => !referencedParents.has(d.id) && !isPacketForm(d),
  );
}

// TODO(TMF-360): this client-side derivation is the deliberate interim. The
// durable design (engine-driven status projection, server-filterable) is
// https://linear.app/bitstorm-technologies/issue/TMF-360/engine-driven-status-projection-filterable-denormalized-status-on
//
// Honest approximation, accepted until TMF-360: the derivation reads only the
// document rows both the list and the detail embed, so a leaf whose match
// consumer is still running reads as "Needs match" until match concludes and
// writes matched_project_id. The detail page could be more precise (it also
// loads the project's tasks, so it knows whether the set-filevine-project-id
// gate is actually open), but ONE derivation shared by both surfaces beats two
// that can disagree — the drift bug this ticket exists to fix.
//
// The review queue (reviewQueue below) is the THIRD consumer of the same
// row-only derivation, and it inherits the same approximation: a document the
// match consumer is still working on enters the queue as "Needs match". The
// queue is honest about that at the point of action rather than in the
// membership — a queued document whose gate task isn't open shows the "the
// system is working on this" state instead of a dead card. TMF-360's
// server-side projection would let membership itself be exact.

/** Where a single LEAF document's remaining work sits — the vocabulary shared
 *  by the per-document chip on the detail and the upload-level badge:
 *    - Filed                → nothing left (metadata.filed_at set)
 *    - Needs match          → unmatched, sitting at the upload's own
 *                             `set filevine project id` gate — actionable HERE
 *    - Awaiting case review → matched into a case; its review gates and filing
 *                             live on the CASE page, not on the upload
 *    - Processing           → matched but not yet case-attached (the sync
 *                             window), or still working its way through */
export type UploadDocumentStatus =
  | "Filed"
  | "Needs match"
  | "Awaiting case review"
  | "Processing";

/** Coarse lifecycle status shown as a badge on the index + detail (TMF-228,
 *  vocabulary rewritten in TMF-357). Mirrors UploadDocumentStatus with
 *  "Complete" standing in for an all-Filed upload. */
export type UploadStatus =
  | "Processing"
  | "Needs match"
  | "Awaiting case review"
  | "Complete";

/** Derive one leaf document's status. Pure on the embedded row — see the
 *  TODO(TMF-360) block above for what that costs us. */
export function documentStatus(document: UploadDocument): UploadDocumentStatus {
  if (document._embedded?.metadata?.filed_at != null) return "Filed";
  if (document._embedded?.metadata?.matched_project_id == null) {
    return "Needs match";
  }
  if (document.case_project_id != null) return "Awaiting case review";
  return "Processing";
}

/** Derive an upload's status from its LEAF documents (pass leafDocuments(upload)
 *  — never droppedDocuments, whose multi-run PDF parents never file and would
 *  strand the badge short of Complete). The same function drives the index and
 *  the detail because both responses embed the documents + their metadata:
 *    - no leaves yet             → Processing (nothing to file yet)
 *    - every leaf filed          → Complete
 *    - any leaf needing a match  → Needs match — it wins over Awaiting case
 *                                  review because the user can act on it right
 *                                  here, on this upload's exception flow
 *    - any leaf on a case        → Awaiting case review (act on the CASE page)
 *    - otherwise                 → Processing (leaves still in flight) */
export function uploadStatus(documents: UploadDocument[]): UploadStatus {
  if (documents.length === 0) return "Processing";
  const statuses = documents.map(documentStatus);
  if (statuses.every((s) => s === "Filed")) return "Complete";
  if (statuses.includes("Needs match")) return "Needs match";
  if (statuses.includes("Awaiting case review")) return "Awaiting case review";
  return "Processing";
}

/** The leaf documents of an upload with work still outstanding — everything
 *  that isn't Filed. Drives the standing "documents awaiting action" flash:
 *  its count, and which upload the flash links to when only one has any. */
export function actionableDocuments(upload: DocumentUpload): UploadDocument[] {
  return leafDocuments(upload).filter((d) => documentStatus(d) !== "Filed");
}

/** The upload's REVIEW QUEUE: the leaf documents a human has to answer for,
 *  in the order the queue walks them (TMF-357). Membership is "a human is
 *  holding this up" — the upload's own exception flow, and the case review gate
 *  presented in place; Filed and Processing are excluded because nobody can act
 *  on them.
 *
 *  The exception arm reads the upload project's TASKS, not the document rows
 *  (TMF-371). A document whose matched_project_id landed but whose gate
 *  completion did not reads as "Processing" off its row alone, so the row-only
 *  queue dropped it and told the reviewer nothing needed review — hiding the one
 *  gate that repairs it. An open gate task puts the document back in the queue
 *  whatever its metadata says.
 *
 *  Order: every match exception first, then every Awaiting case review. Same
 *  precedence uploadStatus applies to the badge, for the same reason — the
 *  match is resolvable right here with no context switch, so it goes first.
 *  Within a group the embedded (created) order is preserved, so a reviewer's
 *  place in the queue doesn't shuffle under them across refetches. */
export function reviewQueue(
  upload: DocumentUpload,
  tasks: Task[],
): UploadDocument[] {
  const leaves = leafDocuments(upload);
  const onException = (d: UploadDocument) =>
    documentStatus(d) === "Needs match" ||
    openMatchGate(tasks, d.id) !== undefined;
  return [
    ...leaves.filter(onException),
    ...leaves.filter(
      (d) => !onException(d) && documentStatus(d) === "Awaiting case review",
    ),
  ];
}

// ── Settle tracking ──────────────────────────────────────────────────
//
// A queue gate action (approve a case review, assign a match) does NOT change
// this upload's document rows: the engine writes matched_project_id / filed_at
// asynchronously, seconds later. Nothing would ever refetch that row on its own
// — the SPA runs staleTime 30s with refetchOnWindowFocus off, and the realtime
// channel has no `document-upload` target type (lib/realtime/clientKeyFor.ts),
// so a queue rendered off a single invalidate-now would freeze on the document
// the human just answered, with its gate card stripped of buttons.
//
// So the queue remembers what it acted on. A settling document renders the
// quiet working state instead of its stale gate card, and while anything is
// settling the upload detail polls. Both end the moment the row moves.

/** How long a document may sit unsettled before the queue stops polling for it.
 *  The quiet state STAYS — an action that never lands is a pipeline problem,
 *  and re-showing a gate card the human already answered invites a double
 *  submit. */
export const SETTLE_TIMEOUT_MS = 90_000;

/** How often the upload detail refetches while anything is settling. */
export const SETTLE_POLL_MS = 2_500;

/** What the queue remembers about a document it just acted on: the status it
 *  held at that moment (the thing that has to change for it to be settled) and
 *  when the action landed (the poll's safety valve). */
export interface SettlingDocument {
  status: UploadDocumentStatus;
  /** ms epoch, from the caller — keeps every function here pure. */
  since: number;
}

export type SettlingDocuments = ReadonlyMap<string, SettlingDocument>;

/** The empty settle set — a shared constant so an untouched queue keeps one
 *  stable identity. */
export const NO_SETTLING: SettlingDocuments = new Map();

/** Remember that a human just acted on this document. */
export function markSettling(
  settling: SettlingDocuments,
  document: UploadDocument,
  now: number,
): SettlingDocuments {
  const next = new Map(settling);
  next.set(document.id, { status: documentStatus(document), since: now });
  return next;
}

/** Drop everything that has settled. A document is settled once it no longer
 *  sits in the queue at the status it was acted on — it filed, or it moved from
 *  "Needs match" to "Awaiting case review". Returns the input untouched when
 *  nothing is being tracked, so an idle queue never churns identities. */
export function pruneSettled(
  settling: SettlingDocuments,
  queue: UploadDocument[],
): SettlingDocuments {
  if (settling.size === 0) return settling;
  const queued = new Map(queue.map((d) => [d.id, documentStatus(d)]));
  const next = new Map<string, SettlingDocument>();
  for (const [id, entry] of settling) {
    if (queued.get(id) === entry.status) next.set(id, entry);
  }
  return next;
}

/** Whether this document's slot should render the quiet working state rather
 *  than its (now answered) gate card. */
export function isSettling(
  settling: SettlingDocuments,
  documentId: string,
): boolean {
  return settling.has(documentId);
}

/** Whether the upload detail should still be polling. False once every settling
 *  document is either settled (pruned) or past the timeout — the valve that
 *  stops a stuck pipeline polling forever. */
export function shouldPollSettling(
  settling: SettlingDocuments,
  now: number,
): boolean {
  for (const entry of settling.values()) {
    if (now - entry.since < SETTLE_TIMEOUT_MS) return true;
  }
  return false;
}

/** Badge color for a document status — reuses the shared status-badge families. */
export function documentStatusVariant(
  status: UploadDocumentStatus,
): BadgeVariant {
  if (status === "Filed") return "success";
  if (status === "Needs match") return "warning";
  if (status === "Awaiting case review") return "info";
  return "neutral"; // Processing
}

/** Badge color for an upload status — reuses the shared status-badge families. */
export function uploadStatusVariant(status: UploadStatus): BadgeVariant {
  if (status === "Complete") return "success";
  if (status === "Needs match") return "warning";
  if (status === "Awaiting case review") return "info";
  return "neutral"; // Processing
}

export type DocumentUploadCollection = HalCollection<DocumentUpload>;

export const DOCUMENT_UPLOADS_KEY = ["document-uploads"] as const;

export const documentUploadsQueryOptions = () =>
  queryOptions<DocumentUploadCollection>({
    queryKey: [...DOCUMENT_UPLOADS_KEY, "list"] as const,
    queryFn: ({ signal }) =>
      halFetch<DocumentUploadCollection>("/api/document-uploads", { signal }),
  });

export const documentUploadQueryKey = (id: string) =>
  [...DOCUMENT_UPLOADS_KEY, id] as const;

/** One document upload — the detail page loads it to resolve its project_id,
 *  then lists that project's tasks (mirrors the case detail page). */
export const documentUploadQueryOptions = (id: string) =>
  queryOptions<DocumentUpload>({
    queryKey: documentUploadQueryKey(id),
    queryFn: ({ signal }) =>
      halFetch<DocumentUpload>(`/api/document-uploads/${id}`, { signal }),
  });
